
import React from "react";
import DOMPurify from "isomorphic-dompurify";
import "./faq-editor.css";

interface FaqPreviewProps {
  question: string;
  answer: string;
  imageUrl: string | null;
  videoUrl: string | null;
}

const FaqPreview = ({ question, answer, imageUrl, videoUrl }: FaqPreviewProps) => {
  // Function to convert YouTube URL to embed URL
  const getYouTubeEmbedUrl = (url: string | null) => {
    if (!url) return null;
    
    // Extract video ID from various YouTube URL formats
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    
    if (match && match[2].length === 11) {
      return `https://www.youtube.com/embed/${match[2]}`;
    }
    
    return null;
  };

  const embedUrl = getYouTubeEmbedUrl(videoUrl);
  
  // Sanitize the HTML content to prevent XSS attacks
  const sanitizedAnswer = DOMPurify.sanitize(answer);

  return (
    <div className="space-y-4">
      <h3 className="text-xl font-semibold text-corporate-primary-100">Preview</h3>
      
      <div className="border rounded-md p-4 bg-gray-50">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Content */}
          <div className="flex-1">
            <h4 className="text-lg font-bold mb-2 text-corporate-primary-100">{question || "Question will appear here"}</h4>
            <div className="prose max-w-none faq-editor-content">
              {answer ? (
                <div dangerouslySetInnerHTML={{ __html: sanitizedAnswer }} />
              ) : (
                <p>Answer will appear here</p>
              )}
            </div>
          </div>
          
          {/* Media */}
          {(imageUrl || embedUrl) && (
            <div className="flex-1 space-y-4">
              {imageUrl && (
                <div>
                  <img src={imageUrl} alt="FAQ illustration" className="rounded-md max-h-64 mx-auto" />
                </div>
              )}
              
              {embedUrl && (
                <div className="aspect-video">
                  <iframe
                    className="w-full h-full rounded-md"
                    src={embedUrl}
                    title="YouTube video player"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      <div className="text-sm text-muted-foreground">
        <p>This is a preview of how the FAQ might appear on the website. The actual display may vary depending on the website design.</p>
      </div>
    </div>
  );
};

export default FaqPreview;
